import { DEMO_PEOPLE, DEMO_TODAY, type IDemoPerson } from '../fixtures';
import {
  qualityRatingLabel,
  type Eligibility,
  type QualityRating,
  type QualityScore,
  type Travel
} from '../vocabulary';
import type { DemoAction } from './actions';

/**
 * Act 2, records: every advisor's record — and so the slice the other screens write to. The
 * firms are the fixtures' alone since SR-414 made Teams and Firms live and their mocks went: no
 * screen changes a firm any more. An initial-review decision changes the record. A person's compliance history is the fixtures' alone since the queues
 * went live (SR-222): survey-api's work items decide nothing here; and since SR-230 no list's save,
 * no import and no ranking's publish writes here either — Lists, Import and the hub's Rankings are
 * survey-api's. Every change to Quality rating or Travel is kept in that field's history with who
 * made it (REC-05).
 */
export interface IAct2State {
  people: IDemoPerson[];
}

export function initialAct2(): IAct2State {
  return { people: DEMO_PEOPLE.map((entry) => ({ ...entry })) };
}

function withQuality(entry: IDemoPerson, quality: Nullable<QualityRating>, by: string): IDemoPerson {
  if (qualityRatingLabel(quality) === qualityRatingLabel(entry.quality)) return entry;
  return {
    ...entry,
    quality,
    qualityHistory: [
      { date: DEMO_TODAY, value: qualityRatingLabel(quality), by },
      ...entry.qualityHistory
    ]
  };
}

function withTravel(entry: IDemoPerson, travel: Nullable<Travel>, by: string): IDemoPerson {
  if (travel === entry.travel) return entry;
  return {
    ...entry,
    travel,
    travelHistory: [{ date: DEMO_TODAY, value: travel ?? '(blank)', by }, ...entry.travelHistory]
  };
}

/** A record out for a reason: the reason in Quality rating, Travel `NO` (§9.1's mapping). */
function excluded(entry: IDemoPerson, reason: string, by: string): IDemoPerson {
  return {
    ...withTravel(withQuality(entry, { reason }, by), 'NO', by),
    eligibility: 'Not eligible'
  };
}

function setEligibility(entry: IDemoPerson, eligibility: Eligibility, reason: string | undefined, by: string) {
  if (eligibility === 'Not eligible' && reason) return { ...excluded(entry, reason, by), reviewed: true };
  if (eligibility === 'Eligible') return { ...withTravel(entry, '1', by), eligibility, reviewed: true };
  return { ...entry, eligibility, reviewed: true };
}

function peopleReducer(people: IDemoPerson[], action: DemoAction): IDemoPerson[] {
  const update = (ids: readonly string[], change: (entry: IDemoPerson) => IDemoPerson) =>
    people.map((entry) => (ids.includes(entry.id) ? change(entry) : entry));

  switch (action.type) {
    case 'eligibility/set':
      return update(action.personIds, (entry) => setEligibility(entry, action.eligibility, action.reason, action.by));

    default:
      return people;
  }
}

export function act2Reducer(state: IAct2State, action: DemoAction): IAct2State {
  const people = peopleReducer(state.people, action);
  return people === state.people ? state : { people };
}
